import { useInView } from '../../hooks/useInView';

interface TrustItem {
  value: string;
  label: string;
  icon: string;
}

interface SectionTrustBarProps {
  items: TrustItem[];
}

export default function SectionTrustBar({ items }: SectionTrustBarProps) {
  const { ref, isVisible } = useInView({ threshold: 0.2 });

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="relative bg-rdr-charcoal border-y border-rdr-gold/20 overflow-hidden"
    >
      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rdr-gold/60 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 lg:py-10">
        {/* Star Rating Row */}
        <div className={`flex justify-center items-center gap-6 mb-8 pb-7 border-b border-white/8 fade-up ${isVisible ? 'is-visible' : ''}`}>
          <div className="flex items-center gap-1.5">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-fill text-base text-rdr-gold" />
              </div>
            ))}
          </div>
          <div className="w-px h-6 bg-white/15" />
          <span className="font-serif text-white text-xl font-medium">5.0</span>
          <div className="w-px h-6 bg-white/15" />
          <p className="text-white/50 font-sans text-xs tracking-[0.18em] uppercase">
            Highly Rated Luxury Venue · Joshua, TX
          </p>
          <div className="hidden lg:flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-google-line text-sm text-rdr-gold" />
            </div>
            <span className="text-white/50 font-sans text-xs">Google Reviews</span>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
          {items.map((item, i) => (
            <div
              key={i}
              className={`flex flex-col items-center text-center py-6 lg:py-5 px-6 fade-up ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${i * 0.12}s` }}
            >
              <div className="w-9 h-9 flex items-center justify-center mb-3">
                <i className={`${item.icon} text-xl text-rdr-gold`} />
              </div>
              <span className="font-serif text-white text-3xl lg:text-4xl font-semibold tracking-tight">
                {item.value}
              </span>
              <div className="w-8 h-px bg-rdr-gold/35 my-2.5" />
              <span className="text-white/40 text-[10px] tracking-[0.22em] uppercase font-sans">
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-rdr-gold/60 to-transparent" />
    </section>
  );
}
